import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Trash2, UserRound, Mail } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fieldRules } from "@/lib/validation";
import { PasswordInput } from "@/components/shop/PasswordInput";

export const inp = "h-11 w-full rounded-xl border border-black/15 bg-white px-3 text-sm outline-none focus:border-amber-500 invalid:[&:not(:placeholder-shown)]:border-rose-400";
export const card = "rounded-2xl border border-black/10 bg-white p-5";

export const AvatarEditor = ({ picture, onChange }) => {
  const { user, checkAuth } = useAuth();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/profile/picture", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.picture); checkAuth(); toast.success("Immagine del profilo aggiornata");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const remove = async () => {
    try { await api.delete("/profile/picture"); onChange(null); checkAuth(); toast.success("Immagine rimossa"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className={`${card} flex flex-wrap items-center gap-5`} data-testid="profile-avatar">
      {picture ? <img src={picture} alt="" className="h-24 w-24 rounded-full border-2 border-black object-cover" data-testid="profile-avatar-img" />
        : <span className="flex h-24 w-24 items-center justify-center rounded-full border-2 border-black bg-violet-500 font-display text-3xl text-white">{user.name?.[0]?.toUpperCase() || <UserRound />}</span>}
      <div className="space-y-2">
        <p className="font-display text-lg font-bold">Immagine del profilo</p>
        <p className="text-xs text-stone-500">JPG, PNG, WEBP o GIF, massimo 8 MB.</p>
        <div className="flex flex-wrap gap-2">
          <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="profile-avatar-input" />
          <button type="button" disabled={busy} onClick={() => ref.current?.click()} className="flex items-center gap-2 rounded-full bg-black px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-stone-800 disabled:opacity-50" data-testid="profile-avatar-upload"><Camera className="h-4 w-4" />{busy ? "Caricamento..." : "Carica foto"}</button>
          {picture && <button type="button" onClick={remove} className="flex items-center gap-2 rounded-full border-2 border-black/20 px-4 py-2 text-sm font-semibold transition-colors hover:border-rose-400 hover:text-rose-600" data-testid="profile-avatar-remove"><Trash2 className="h-4 w-4" />Rimuovi</button>}
        </div>
      </div>
    </div>
  );
};

export const PersonalForm = ({ p }) => {
  const { checkAuth } = useAuth();
  const [f, setF] = useState({ first_name: p.first_name || "", last_name: p.last_name || "", phone: p.phone || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try { await api.put("/profile", { ...f, phone: f.phone.trim() || null }); checkAuth(); toast.success("Dati personali salvati"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={save} className={`${card} grid gap-3 sm:grid-cols-2`} data-testid="profile-personal-form">
      <p className="font-display text-lg font-bold sm:col-span-2">Dati personali</p>
      {[["first_name", "Nome", "given-name"], ["last_name", "Cognome", "family-name"]].map(([k, l, ac]) => (
        <label key={k} className="space-y-1"><span className="text-xs text-stone-600">{l}</span>
          <input required placeholder=" " value={f[k]} onChange={set(k)} {...fieldRules(k)} autoComplete={ac} minLength={2} className={inp} data-testid={`profile-${k}`} /></label>
      ))}
      <label className="space-y-1 sm:col-span-2"><span className="text-xs text-stone-600">Telefono (facoltativo)</span>
        <input value={f.phone} onChange={set("phone")} {...fieldRules("phone")} required={false} placeholder="+39 333 1234567" className={inp} data-testid="profile-phone" /></label>
      <button className="h-11 rounded-full bg-amber-500 font-bold text-black transition-transform hover:-translate-y-0.5 sm:col-span-2" data-testid="profile-personal-save">Salva dati</button>
    </form>
  );
};

export const EmailChange = ({ p, onChange }) => {
  const [f, setF] = useState({ new_email: "", password: "" });
  const [busy, setBusy] = useState(false);
  if (p.auth_provider === "google") return (
    <div className={card} data-testid="profile-email-google"><p className="flex items-center gap-2 font-display text-lg font-bold"><Mail className="h-5 w-5" />Email</p>
      <p className="mt-1 text-sm text-stone-600"><b data-no-translate>{p.email}</b> · accedi con Google, quindi l'email è quella del tuo account Google.</p></div>
  );
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/profile/email", f); toast.success(`Ti abbiamo mandato un link di conferma a ${data.pending_email}`); setF({ new_email: "", password: "" }); onChange(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const cancel = async () => { await api.delete("/profile/email"); toast.success("Cambio email annullato"); onChange(); };
  return (
    <form onSubmit={submit} className={`${card} grid gap-3 sm:grid-cols-2`} data-testid="profile-email-form">
      <p className="flex items-center gap-2 font-display text-lg font-bold sm:col-span-2"><Mail className="h-5 w-5" />Email</p>
      <p className="text-sm text-stone-600 sm:col-span-2">Email attuale: <b data-no-translate data-testid="profile-email-current">{p.email}</b></p>
      {p.pending_email && (
        <p className="flex flex-wrap items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900 sm:col-span-2" data-testid="profile-email-pending">
          In attesa di conferma: <b data-no-translate>{p.pending_email}</b>. Apri il link che ti abbiamo mandato.
          <button type="button" onClick={cancel} className="text-xs underline" data-testid="profile-email-cancel">Annulla</button>
        </p>
      )}
      <label className="space-y-1"><span className="text-xs text-stone-600">Nuova email</span>
        <input required type="email" placeholder=" " value={f.new_email} onChange={(e) => setF({ ...f, new_email: e.target.value })} autoComplete="email" className={inp} data-testid="profile-email-new" /></label>
      <label className="space-y-1"><span className="text-xs text-stone-600">Password attuale</span>
        <PasswordInput required placeholder=" " value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} autoComplete="current-password" className={inp} data-testid="profile-email-password" /></label>
      <p className="text-xs text-stone-500 sm:col-span-2">Fino alla conferma continui ad accedere con l'email attuale.</p>
      <button disabled={busy} className="h-11 rounded-full border-2 border-black font-bold transition-colors hover:bg-black hover:text-white disabled:opacity-50 sm:col-span-2" data-testid="profile-email-submit">{busy ? "Invio..." : "Cambia email"}</button>
    </form>
  );
};
